import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, interval, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService, AppUser } from './auth.service';
import { LoggerService } from './logger.service';

export interface SessionState {
  user: AppUser | null;
  isAuthenticated: boolean;
  lastActivity: number;
  isIdle: boolean;
  isExpired: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SessionManagerService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  private readonly IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 min inactividad
  private readonly MAX_SESSION_MS = 24 * 60 * 60 * 1000; // 24h sesion maxima
  private readonly ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;

  private stateSubject = new BehaviorSubject<SessionState>({
    user: null,
    isAuthenticated: false,
    lastActivity: Date.now(),
    isIdle: false,
    isExpired: false,
  });

  state$ = this.stateSubject.asObservable();
  isAuthenticated$ = this.state$.pipe(
    filter(state => state.isAuthenticated !== undefined),
    filter(state => state.isAuthenticated)
  );

  private destroy$ = new Subject<void>();
  private idleCheckSub?: Subscription;

  constructor() {
    this.init();
  }

  private init(): void {
    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.updateState({ user, isAuthenticated: true, lastActivity: Date.now(), isIdle: false, isExpired: false });
        if (user.status === 'disabled') {
          this.forceLogout('account_disabled').catch(() => {});
        }
      } else {
        this.updateState({ user: null, isAuthenticated: false });
      }
    });

    this.bindActivityEvents();

    this.idleCheckSub = interval(60000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkIdleAndExpiry();
    });
  }

  getState(): SessionState {
    return this.stateSubject.value;
  }

  touchActivity(): void {
    const state = this.stateSubject.value;
    if (state.isIdle || state.isExpired) {
      this.updateState({ ...state, lastActivity: Date.now(), isIdle: false });
    } else {
      this.updateState({ ...state, lastActivity: Date.now() });
    }
  }

  async forceLogout(reason?: string): Promise<void> {
    const state = this.stateSubject.value;
    if (reason) {
      this.logger.warn(`[SessionManager] Force logout: ${reason}`);
    }
    this.updateState({ user: null, isAuthenticated: false, lastActivity: Date.now(), isIdle: false, isExpired: true });
    try {
      await this.authService.logout();
    } catch {
      // Ignore logout errors during force logout
    }
    this.router.navigate(['/login'], {
      queryParams: reason ? { session_expired: '1', reason } : { session_expired: '1' },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.idleCheckSub?.unsubscribe();
    this.unbindActivityEvents();
  }

  private updateState(partial: Partial<SessionState>): void {
    const current = this.stateSubject.value;
    this.stateSubject.next({ ...current, ...partial });
  }

  private checkIdleAndExpiry(): void {
    const state = this.stateSubject.value;
    if (!state.user) return;

    const now = Date.now();
    const idleTime = now - state.lastActivity;

    if (idleTime > this.IDLE_TIMEOUT_MS && !state.isIdle) {
      this.updateState({ isIdle: true });
      this.logger.debug(`[SessionManager] User idle for ${Math.round(idleTime / 60000)}min`);
    }
  }

  private bindActivityEvents(): void {
    const handler = () => this.touchActivity();
    for (const event of this.ACTIVITY_EVENTS) {
      window.addEventListener(event, handler, { passive: true });
    }
    (window as any).__sessionActivityHandler = handler;
  }

  private unbindActivityEvents(): void {
    const handler = (window as any).__sessionActivityHandler;
    if (handler) {
      for (const event of this.ACTIVITY_EVENTS) {
        window.removeEventListener(event, handler);
      }
      delete (window as any).__sessionActivityHandler;
    }
  }
}
